import React, { useState } from 'react';
import {
  Search, Loader2, User as UserIcon, Mail, Calendar, BookOpen,
  RotateCcw, AlertTriangle, CheckCircle2, XCircle, Hourglass, Copy, Check,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  useGetAdminUsersQuery,
  useGetAdminUserOrdersQuery,
  useAdminRefundOrderMutation,
} from '../../api/apiSlice';
import Loading from '../../components/common/Loading';

// 0 ₸ — реальная сумма (не «бесплатно»).
const fmt = (n) => `${(Number(n) || 0).toLocaleString('ru-RU')} ₸`;
const fmtDateTime = (iso) =>
  new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short', year: 'numeric' });

// ─── StatusBadge ─────────────────────────────────────────────────────
const STATUS_CFG = {
  confirmed:       { label: 'Оплачен',     cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300', Icon: CheckCircle2 },
  paid:            { label: 'Оплачен',     cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300', Icon: CheckCircle2 },
  pending_contact: { label: 'Ждёт связи',  cls: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',          Icon: Hourglass },
  pending:         { label: 'Ожидает',     cls: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',          Icon: Hourglass },
  cancelled:       { label: 'Отменён',     cls: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',                 Icon: XCircle },
  expired:         { label: 'Истёк',       cls: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',                 Icon: XCircle },
  failed:          { label: 'Ошибка',      cls: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300',                  Icon: XCircle },
  refunded:        { label: 'Возврат',     cls: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300',                  Icon: RotateCcw },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CFG[status] || { label: status, cls: 'bg-gray-100 text-gray-700', Icon: Hourglass };
  const { Icon } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${cfg.cls}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
};

// ─── CopyButton ─────────────────────────────────────────────────────
function CopyButton({ value, label = 'Скопировать' }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
      className="p-1 rounded text-gray-400 hover:text-indigo-600 transition"
      title={label}
    >
      {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}

// ─── OrderRow — одна строка заказа в карточке студента ───────────────
function OrderRow({ order }) {
  const [refundOrder, { isLoading: refunding }] = useAdminRefundOrderMutation();
  const [showRefund, setShowRefund] = useState(false);
  const [reason, setReason] = useState('');

  const canRefund = order.status === 'confirmed';

  const handleRefund = async () => {
    if (!reason.trim()) {
      toast.error('Укажите причину возврата');
      return;
    }
    if (!window.confirm(
      `Точно вернуть оплату ${fmt(order.amount)}?\n\n` +
      `• Доступ к курсу будет отозван\n` +
      `• Долю инструктора ${fmt(order.instructorShare)} снимаем с его баланса\n` +
      `• Деньги переведите вручную в Kaspi`
    )) return;
    try {
      await refundOrder({ orderId: order.id, reason: reason.trim() }).unwrap();
      toast.success('Возврат оформлен. Не забудьте перевести деньги.');
      setShowRefund(false);
      setReason('');
    } catch (err) {
      toast.error(err?.data?.message || 'Ошибка');
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <StatusBadge status={order.status} />
            <span className="text-[10px] text-gray-400 dark:text-gray-500 font-mono">{order.id}</span>
            <CopyButton value={order.id} label="ID заказа" />
          </div>
          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {order.course?.title || '—'}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Инструктор: {order.course?.instructor?.name || '—'}
            {order.course?.instructor?.email && ` · ${order.course.instructor.email}`}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-base font-semibold text-gray-900 dark:text-white tabular-nums">
            {fmt(order.amount)}
          </div>
          <div className="text-[10px] text-gray-500 mt-0.5 tabular-nums">
            доля инстр.: {fmt(order.instructorShare)}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400">
        <span>Создан: {fmtDateTime(order.createdAt)}</span>
        {order.confirmedAt && <span>Оплачен: {fmtDateTime(order.confirmedAt)}</span>}
        {order.rejectedAt && <span className="text-red-600 dark:text-red-400">Закрыт: {fmtDateTime(order.rejectedAt)}</span>}
        {order.rejectionReason && (
          <span className="italic">«{order.rejectionReason}»</span>
        )}
        {order.payout?.status === 'paid' && order.payout.paidAt && (
          <span className="text-emerald-600 dark:text-emerald-400">
            Выплачено инстр.: {fmtDate(order.payout.paidAt)}
          </span>
        )}
        {order.payout?.status === 'pending' && (
          <span className="text-amber-600 dark:text-amber-400">
            В заявке на выплату
          </span>
        )}
      </div>

      {/* Кнопка возврата — только для confirmed-заказов. */}
      {canRefund && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
          {!showRefund ? (
            <button
              type="button"
              onClick={() => setShowRefund(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 dark:border-red-900/40 bg-red-50 dark:bg-red-950/20 px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-950/40"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Сделать возврат
            </button>
          ) : (
            <div className="rounded-xl bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/40 p-3 space-y-2">
              <div className="flex items-start gap-2 text-xs text-red-800 dark:text-red-200">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                  Возврат отзовёт доступ студента к курсу и снимет долю инструктора
                  с баланса. <b>Деньги нужно перевести в Kaspi вручную</b> —
                  система только обновит запись в БД.
                </div>
              </div>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Причина возврата (увидит инструктор в аудит-логе)"
                className="w-full rounded-lg border border-red-300 dark:border-red-800 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleRefund}
                  disabled={refunding}
                  className="flex-1 rounded-lg bg-red-600 hover:bg-red-700 text-white py-2 text-sm font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                >
                  {refunding ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                  Вернуть {fmt(order.amount)}
                </button>
                <button
                  onClick={() => { setShowRefund(false); setReason(''); }}
                  className="rounded-lg border border-red-300 dark:border-red-700 px-3 py-2 text-sm text-gray-600 dark:text-gray-300"
                >
                  Отмена
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── StudentDetails — расширенная карточка после выбора ──────────────
function StudentDetails({ userId, onClose }) {
  const { data, isLoading, error } = useGetAdminUserOrdersQuery(userId);

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8 text-center">
        <Loader2 className="h-6 w-6 animate-spin mx-auto text-indigo-600 mb-2" />
        <p className="text-sm text-gray-500">Загрузка заказов студента…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/20 p-4 text-red-700 dark:text-red-300 text-sm">
        {error?.data?.message || 'Не удалось загрузить заказы'}
      </div>
    );
  }

  const user = data?.user || {};
  const orders = data?.orders || [];
  const confirmedCount = orders.filter((o) => o.status === 'confirmed').length;
  const refundedCount = orders.filter((o) => o.status === 'refunded').length;

  return (
    <div className="space-y-4">
      {/* Header студента */}
      <div className="rounded-2xl border border-indigo-200 dark:border-indigo-900/40 bg-indigo-50 dark:bg-indigo-950/20 p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div className="text-lg font-bold text-gray-900 dark:text-white truncate">
              {user.name || '—'}
            </div>
            <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300 mt-0.5">
              <Mail className="h-3.5 w-3.5" />
              <span className="truncate">{user.email || '—'}</span>
              {user.email && <CopyButton value={user.email} label="Email" />}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mt-1.5 font-mono">
              <span>ID: {user._id}</span>
              <CopyButton value={user._id} label="ID студента" />
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-gray-500 dark:text-gray-400 hover:underline shrink-0"
          >
            Закрыть
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div className="rounded-xl bg-white dark:bg-gray-900 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Регистрация</div>
            <div className="font-semibold text-gray-900 dark:text-white text-xs mt-0.5 flex items-center gap-1">
              <Calendar className="h-3 w-3" /> {user.createdAt ? fmtDate(user.createdAt) : '—'}
            </div>
          </div>
          <div className="rounded-xl bg-white dark:bg-gray-900 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Курсов</div>
            <div className="font-semibold text-gray-900 dark:text-white text-xs mt-0.5 flex items-center gap-1">
              <BookOpen className="h-3 w-3" /> {user._count?.enrollments ?? 0}
            </div>
          </div>
          <div className="rounded-xl bg-white dark:bg-gray-900 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Оплачено</div>
            <div className="font-semibold text-emerald-700 dark:text-emerald-400 text-xs mt-0.5">
              {confirmedCount}
            </div>
          </div>
          <div className="rounded-xl bg-white dark:bg-gray-900 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Возвратов</div>
            <div className="font-semibold text-red-700 dark:text-red-400 text-xs mt-0.5">
              {refundedCount}
            </div>
          </div>
        </div>
      </div>

      {/* Список заказов */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
          Заказы ({orders.length})
        </h3>
        {orders.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 p-10 text-center text-gray-500 dark:text-gray-400">
            <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-40" />
            У студента нет заказов
          </div>
        ) : (
          <div className="space-y-2">
            {orders.map((o) => <OrderRow key={o.id} order={o} />)}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────
export default function AdminStudentLookup() {
  const [query, setQuery] = useState('');
  // Debounce поиска — иначе на каждый ввод буквы летит запрос. 300ms — норм.
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const {
    data: usersData,
    isFetching,
  } = useGetAdminUsersQuery(
    { search: debouncedQuery, limit: 10, page: 1 },
    { skip: !debouncedQuery }
  );

  // Бэк отдаёт {items, total, page...} (не {users}). Раньше тут было usersData?.users
  // и список всегда был пустой даже на валидном поиске по cuid.
  const users = usersData?.items || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
          Поиск студента
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Найди студента по ID / email / имени — увидишь его заказы и сможешь оформить возврат
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="ID студента, email или имя…"
          autoFocus
          className="w-full pl-10 pr-12 py-3 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        {isFetching && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-indigo-600" />
        )}
      </div>

      {/* Если выбран студент — показываем его карточку. Иначе список результатов. */}
      {selectedUserId ? (
        <StudentDetails
          userId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
        />
      ) : !debouncedQuery ? (
        <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 p-12 text-center text-gray-500 dark:text-gray-400">
          <UserIcon className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p>Начни вводить — найду студента в БД</p>
          <p className="text-xs mt-1">По ID — точное совпадение, по email/имени — частичное</p>
        </div>
      ) : users.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 p-12 text-center text-gray-500 dark:text-gray-400">
          Никого не нашли. Проверь правильность email/имени или используй полный ID.
        </div>
      ) : (
        <div className="space-y-2">
          {users.map((u) => (
            <button
              key={u._id || u.id}
              type="button"
              onClick={() => setSelectedUserId(u._id || u.id)}
              className="w-full text-left rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 hover:border-indigo-400 dark:hover:border-indigo-500 transition"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {u.name || '—'}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {u.email}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Курсов: <b className="text-gray-900 dark:text-white">{u.enrollmentsCount ?? 0}</b>
                  </div>
                  <div className="text-[10px] text-gray-400 mt-0.5 font-mono">{u._id || u.id}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
